import { DatePipe, NgFor, NgIf } from '@angular/common';
import { Component, Inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormArray, FormBuilder, FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import {
  DialogData,
  GraphDimension,
  HourlyTimeSlots,
  OfferDimensionVm,
  RequestBodyInterface,
  RequestChangeData,
  TimeSlotData,
  TimeSlotSeriesData
} from '@dr-customer-offers-ui/lib-interfaces';
import { Store } from '@ngrx/store';
import { TranslateModule } from '@ngx-translate/core';
import moment from 'moment-timezone';
import { AppState } from '../../../../../app/store';
import { IconsComponent } from '../../../../shared/icons/icons.component';
import { FindBySeriesNamePipe } from '../../../../shared/pipes/find-by-series-name.pipe';
import * as Selectors from '../../../../store/selectors/com.selectors';

@Component({
  selector: 'dr-customer-offers-ui-dialog',
  templateUrl: './dialog.component.html',
  styleUrls: ['./dialog.component.scss'],
  standalone: true,
  imports: [
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatSelectModule,
    MatIconModule,
    NgFor,
    NgIf,
    DatePipe,
    ReactiveFormsModule,
    FormsModule,
    IconsComponent,
    TranslateModule,
    MatCheckboxModule
  ]
})
export class GraphEditDialogComponent {
  currentHourIndex = 0;
  currentHourData: HourlyTimeSlots;
  protected changes: Map<string, any> = new Map();
  protected regConfig = toSignal(this.store.select(Selectors.selectCurrentRegViewConfig));
  protected findBySeriesNamePipe = new FindBySeriesNamePipe();
  readonly demandMinEditValue: number;
  readonly demandMaxEditValue: number;
  readonly initialOptOut: boolean[] = [];

  protected offerForm: FormGroup;
  protected seriesLabels: string[] = [];
  protected dimensions: OfferDimensionVm[] = [];
  isOptOutEnabled = true;
  isConsolidatedToHourly = false;

  readonly FormErrorType = FormErrorType;

  constructor(
    private store: Store<AppState>,
    public dialogRef: MatDialogRef<GraphEditDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: DialogData,
    private fb: FormBuilder
  ) {
    this.isOptOutEnabled = this.data.isOptOutEnabled;
    this.isConsolidatedToHourly = this.data.isConsolidatedToHourly;
    // Find the initial hour index based on clicked time
    const clickedHour = this.data.clickedTime.hour();

    this.currentHourIndex = this.data.allHoursData.findIndex((hour: HourlyTimeSlots) => hour.hour === clickedHour);
    if (this.currentHourIndex === -1) {
      this.currentHourIndex = 0; // Fallback to first hour
    }
    this.currentHourData = this.data.allHoursData[this.currentHourIndex];

    const regConfig = this.regConfig();
    this.seriesLabels = this.currentHourData.timeSlots[0].data.map((m) => m.seriesName);

    this.demandMinEditValue = regConfig?.minimumValue ?? 0;
    this.demandMaxEditValue = regConfig?.maximumValue ?? 99999;

    const META: Record<string, { seriesName: string; labelKey: string; unit: string; noteKey?: string }> = {
      DEMAND_FORECAST: { seriesName: 'Demand', labelKey: 'COM.TABS.OPEN.CHART.DEMAND', unit: 'kW', noteKey: 'COM.TABS.OPEN.CHART.DEMAND_NOTE' },
      kW: { seriesName: 'Capacity', labelKey: 'COM.TABS.OPEN.CHART.CAPACITY', unit: 'kW' },
      PRICE: { seriesName: 'Price', labelKey: 'COM.TABS.OPEN.CHART.PRICE', unit: this.data.currency },
      KW_TO_INCREASE: { seriesName: 'Capacity Increase', labelKey: 'COM.TABS.OPEN.CHART.CAPACITY_INCREASE', unit: 'kW' },
      KW_TO_DECREASE: { seriesName: 'Capacity Decrease', labelKey: 'COM.TABS.OPEN.CHART.CAPACITY_DECREASE', unit: 'kW' },
      PRICE_TO_INCREASE: { seriesName: 'Price Increase', labelKey: 'COM.TABS.OPEN.CHART.PRICE_INCREASE', unit: this.data.currency },
      PRICE_TO_DECREASE: { seriesName: 'Price Decrease', labelKey: 'COM.TABS.OPEN.CHART.PRICE_DECREASE', unit: this.data.currency }
    };

    const validatorsFor = (key: string) => {
      if (key === 'DEMAND_FORECAST') return [Validators.required];
      if (key === 'kW') return [Validators.min(this.demandMinEditValue), Validators.max(this.demandMaxEditValue)];
      if (key === 'PRICE') return [Validators.required, Validators.min(0)];
      // KW_TO_INCREASE, KW_TO_DECREASE, PRICE_TO_INCREASE, PRICE_TO_DECREASE — no min/max
      return [];
    };

    const errorMetaFor = (key: string) => {
      if (key === 'DEMAND_FORECAST')
        return {
          errorRangeKey: 'COM.TABS.OPEN.CHART.DEMAND_RANGE',
          errorRequiredKey: 'COM.TABS.OPEN.CHART.DEMAND_REQUIRED',
          rangeMin: null,
          rangeMax: null
        };
      if (key === 'kW')
        return {
          errorRangeKey: 'COM.TABS.OPEN.CHART.CAPACITY_RANGE',
          errorRequiredKey: '',
          rangeMin: this.demandMinEditValue,
          rangeMax: this.demandMaxEditValue
        };
      if (key === 'PRICE')
        return {
          errorRangeKey: 'COM.TABS.OPEN.CHART.PRICE_MIN',
          errorRequiredKey: 'COM.TABS.OPEN.CHART.PRICE_REQUIRED',
          rangeMin: 0,
          rangeMax: null
        };
      return { errorRangeKey: '', errorRequiredKey: '', rangeMin: null, rangeMax: null };
    };

    const DEMAND_FIRST: GraphDimension = 'DEMAND_FORECAST';
    const offerDimensions: GraphDimension[] = regConfig?.graph_config?.offer_dimensions ?? ['DEMAND_FORECAST', 'kW', 'PRICE'];
    const orderedDimensions: GraphDimension[] = offerDimensions.includes(DEMAND_FIRST)
      ? [DEMAND_FIRST, ...offerDimensions.filter((key: GraphDimension) => key !== DEMAND_FIRST)]
      : offerDimensions;
    this.dimensions = orderedDimensions
      .filter((key: GraphDimension) => META[key])
      .map((key: GraphDimension) => ({ key, ...META[key], ...errorMetaFor(key) }));

    const group: Record<string, FormArray> = {};
    for (const dim of this.dimensions) {
      const controls = this.isDimensionConsolidated(dim.key)
        ? [new FormControl(this.findBySeriesNamePipe.transform(dim.seriesName, this.currentHourData.timeSlots[0].data), validatorsFor(dim.key))]
        : this.currentHourData.timeSlots.map(
            (slot) => new FormControl(this.findBySeriesNamePipe.transform(dim.seriesName, slot.data), validatorsFor(dim.key))
          );
      group[dim.key] = this.fb.array(controls);
    }
    this.offerForm = this.fb.group(group);
    if (this.isOptOutEnabled) {
      this.initialOptOut = this.currentHourData.timeSlots.map((slot) => !!slot.offer_opt_out_state);

      this.offerForm.addControl('optOut', this.fb.array(this.initialOptOut.map((v) => new FormControl<boolean>(v, { nonNullable: true }))));
    }
  }

  getSlots(key: string): FormArray {
    return this.offerForm.get(key) as FormArray;
  }

  get optOutArray(): FormArray {
    return (this.offerForm.get('optOut') as FormArray) ?? this.fb.array([]);
  }

  get optOutChanged(): boolean {
    if (!this.isOptOutEnabled) return false;
    return this.optOutArray.controls.some((c, i) => c.value !== this.initialOptOut[i]);
  }
  get hasPendingChanges(): boolean {
    return this.changes.size > 0 || this.optOutChanged;
  }

  isDimensionConsolidated(key: string): boolean {
    const consolidate = this.regConfig()?.graph_config?.consolidate_dimensions_into_hourly ?? [];
    return consolidate.includes(key as any);
  }

  protected getLastSlotEndTime(hourData: HourlyTimeSlots): string {
    const lastSlot = hourData.timeSlots[hourData.timeSlots.length - 1];
    return lastSlot.formattedTime;
  }

  getFirstFormArrayError(formArray: FormArray): FormErrorType {
    const errorTypes = [FormErrorType.required, FormErrorType.min, FormErrorType.max]; // Priority order

    for (const errorType of errorTypes) {
      if (formArray.controls.some((control) => control.hasError(errorType))) {
        return errorType;
      }
    }

    return FormErrorType.none; // No errors
  }

  /**
   * Update value with start/end timestamps from the time slot
   */
  updateValue(time: moment.Moment, seriesName: string, key: string, event: any, currenttime: HourlyTimeSlots): void {
    const newValue = Number(event.target.value);

    if (this.isDimensionConsolidated(key)) {
      // Fan out: write same value to ALL time slots in the hour
      currenttime.timeSlots.forEach((slot) => {
        const key = `${moment.utc(slot.time.valueOf()).valueOf()}_${seriesName}`;
        this.changes.set(key, {
          time: moment.utc(slot.time.valueOf()),
          seriesName,
          newValue,
          hour: moment.utc(slot.time.valueOf()).hour(),
          min: moment.utc(slot.time.valueOf()).minute()
        });
      });
    } else {
      // Existing per-slot logic
      const key = `${moment.utc(time.valueOf()).valueOf()}_${seriesName}`;
      this.changes.set(key, {
        time: moment.utc(time.valueOf()),
        seriesName,
        newValue,
        hour: moment.utc(time.valueOf()).hour(),
        min: moment.utc(time.valueOf()).minute()
      });
    }

    this.getSlots(key).markAllAsTouched();
  }

  getChangesArray(): RequestChangeData[] {
    return Array.from(this.changes.values()).sort((a, b) => a.time.valueOf() - b.time.valueOf());
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  onSave(): void {
    if (this.offerForm.invalid) {
      this.offerForm.markAllAsTouched();
      return;
    }

    if (!this.hasPendingChanges) {
      this.dialogRef.close();
      return;
    }

    const dimensionChanges = this.getChangesArray();
    const anyOptedOut = this.isOptOutEnabled && this.optOutArray.controls.some((c) => c.value === true);
    const data: RequestBodyInterface[] =
      anyOptedOut || this.optOutChanged ? this.buildCurrentHourRequest() : this.processChangesForRequest(dimensionChanges);

    this.dialogRef.close({
      changes: dimensionChanges,
      totalChanges: dimensionChanges.length,
      affectedHours: [...new Set(dimensionChanges.map((c) => c.hour))],
      clickedTime: this.data.clickedTime,
      data
    });
  }

  private buildCurrentHourRequest(): RequestBodyInterface[] {
    const edits = this.getChangesArray();
    const CAPACITY_KEYS = ['kW', 'KW_TO_INCREASE', 'KW_TO_DECREASE'];

    return this.currentHourData.timeSlots.map((slot: TimeSlotData, i: number) => {
      const optedOut = this.optOutArray.at(i)?.value === true;
      const start = moment.utc(slot.time.valueOf());
      const end = start.clone().add(this.data.metadata.frequency, 'minutes');

      const offerValue: Record<string, number> = {};
      for (const dim of this.dimensions) {
        const sd = slot.data.find((d: TimeSlotSeriesData) => d.seriesName === dim.seriesName);
        if (sd?.value != null) offerValue[dim.key] = sd.value;
        else if (optedOut && CAPACITY_KEYS.includes(dim.key)) offerValue[dim.key] = 0;
      }

      edits
        .filter((c: RequestChangeData) => c.time.isSame(slot.time, 'minute'))
        .forEach((c: RequestChangeData) => {
          const dim = this.dimensions.find((d: OfferDimensionVm) => d.seriesName === c.seriesName);
          if (dim) offerValue[dim.key] = c.newValue;
        });

      return {
        is_emergency: false,
        offer_opt_out_state: optedOut,
        offer_start_dttm_utc: start.toISOString(),
        offer_end_dttm_utc: end.toISOString(),
        offer_value: offerValue
      };
    });
  }

  getEndTime(start: string, frequency: number): string {
    const timezone = this.regConfig()?.timeZone ?? 'UTC';
    const baseDate = this.data.metadata.date.clone().tz(timezone, true);
    const dateStr = baseDate.format('YYYY-MM-DD');
    const startMoment = moment.tz(`${dateStr} ${start}:00`, 'YYYY-MM-DD HH:mm:ss', timezone);
    const endMoment = startMoment.add(frequency, 'minutes');

    return endMoment.format('HH:mm');
  }

  private processChangesForRequest(changes: RequestChangeData[]): RequestBodyInterface[] {
    const groupedByTime = new Map<string, RequestChangeData[]>();

    changes.forEach((change: RequestChangeData) => {
      const startTime = change.time.clone().utc().toISOString();
      if (!groupedByTime.has(startTime)) {
        groupedByTime.set(startTime, []);
      }
      groupedByTime.get(startTime)?.push(change);
    });

    // Process each unique time slot
    return Array.from(groupedByTime.entries()).map(([startTime, timeChanges]: [string, RequestChangeData[]]) => {
      const firstChange = timeChanges[0];
      const endTime = firstChange.time.clone().add(this.data.metadata.frequency, 'minutes').utc().toISOString();

      const offerValue: Record<string, number> = {};

      timeChanges.forEach((change) => {
        const dim = this.dimensions.find((d) => d.seriesName === change.seriesName);
        if (dim) {
          offerValue[dim.key] = change.newValue;
        }
      });

      const currentTimeSlot = this.findTimeSlotInCurrentHour(firstChange.time);

      if (currentTimeSlot) {
        for (const dim of this.dimensions) {
          if (offerValue[dim.key] === undefined || offerValue[dim.key] === null) {
            const seriesData = currentTimeSlot.data.find((d) => d.seriesName === dim.seriesName);
            if (seriesData?.value !== null && seriesData?.value !== undefined) {
              offerValue[dim.key] = seriesData.value;
            }
          }
        }
      }

      return {
        is_emergency: false,
        offer_opt_out_state: false,
        offer_end_dttm_utc: endTime,
        offer_start_dttm_utc: startTime,
        offer_value: offerValue
      };
    });
  }

  private findTimeSlotInCurrentHour(targetTime: moment.Moment): TimeSlotData | null {
    if (!this.currentHourData?.timeSlots) {
      return null;
    }

    // Find the time slot that matches the target time
    return (
      this.currentHourData.timeSlots.find((slot: TimeSlotData) => {
        return slot.time.isSame(targetTime, 'minute');
      }) || null
    );
  }
}

enum FormErrorType {
  none = 'none',
  required = 'required',
  min = 'min',
  max = 'max'
}
